import React, { useState, useRef, useEffect } from "react";
import {
    Send, Info, MessageSquare, Mail, Phone, UserCheck, X,
    Camera, Video, Paperclip, Square, FileIcon, Film, Loader2, Zap, ChevronDown, Sparkles,
} from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { UserAvatar } from "../shared/UserAvatar";
import { StatusBadge } from "../shared/Badge";
import { toast } from "sonner";
import * as api from "../../lib/api";

// Return current date/time as local YYYY-MM-DDTHH:MM (for datetime-local inputs)
function localNow() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

const CONTACT_TYPES = {
    email: { icon: Mail, label: "E-mail envoyé", color: "bg-blue-50 border-blue-200 text-blue-700" },
    phone: { icon: Phone, label: "Appel téléphonique", color: "bg-green-50 border-green-200 text-green-700" },
    visit: { icon: UserCheck, label: "Visite en personne", color: "bg-purple-50 border-purple-200 text-purple-700" },
};

export function TicketComments({ ticketId, ticket, comments, isAgentOrAdmin, currentUser, getUserById, formatRelativeTime, onRefresh, aiEnabled }) {
    const [newComment, setNewComment] = useState("");
    const [isInternalComment, setIsInternalComment] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const [contactMode, setContactMode] = useState(null);
    const [contactSummary, setContactSummary] = useState("");
    const [contactAt, setContactAt] = useState("");
    const [submittingContact, setSubmittingContact] = useState(false);
    // 7C — Macros
    const [macros, setMacros] = useState([]);
    const [showMacroMenu, setShowMacroMenu] = useState(false);
    const [applyingMacro, setApplyingMacro] = useState(false);
    const macroMenuRef = useRef(null);
    const [reformulatingComment, setReformulatingComment] = useState(false);
    const [reformulatingContact, setReformulatingContact] = useState(false);

    useEffect(() => {
        if (isAgentOrAdmin) {
            api.getMacros().then(setMacros).catch((err) => console.warn("Chargement des macros impossible:", err));
        }
    }, [isAgentOrAdmin]);

    // Close macro menu on outside click
    useEffect(() => {
        const handler = (e) => {
            if (macroMenuRef.current && !macroMenuRef.current.contains(e.target)) setShowMacroMenu(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleApplyMacro = async (macro) => {
        try {
            setApplyingMacro(true);
            setShowMacroMenu(false);
            await api.applyMacro(ticketId, macro.id, currentUser.id);
            toast.success(`Macro "${macro.name}" appliquée`);
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'application de la macro");
        } finally {
            setApplyingMacro(false);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() && pendingFiles.length === 0) return;
        try {
            setSubmitting(true);
            if (pendingFiles.length > 0) {
                setUploading(true);
                for (const pf of pendingFiles) {
                    await api.uploadAttachment(ticketId, pf.file, currentUser.id);
                }
                setUploading(false);
            }
            if (newComment.trim()) {
                await api.createComment(ticketId, { content: newComment, is_internal: isInternalComment }, currentUser.id);
            } else if (pendingFiles.length > 0) {
                const fileNames = pendingFiles.map((f) => f.file.name).join(", ");
                await api.createComment(ticketId, { content: `Pièce(s) jointe(s) ajoutée(s) : ${fileNames}`, is_internal: isInternalComment }, currentUser.id);
            }
            setNewComment("");
            setIsInternalComment(false);
            setPendingFiles([]);
            toast.success(pendingFiles.length > 0 ? "Commentaire et pièces jointes envoyés" : "Commentaire ajouté");
            onRefresh();
        } catch { toast.error("Erreur lors de l'envoi"); }
        finally { setSubmitting(false); setUploading(false); }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        const newPending = files.map((file) => ({
            id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file,
            preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
            isVideo: file.type.startsWith("video/"),
        }));
        setPendingFiles((prev) => [...prev, ...newPending]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removePendingFile = (pendingId) => {
        setPendingFiles((prev) => {
            const removed = prev.find((f) => f.id === pendingId);
            if (removed?.preview) URL.revokeObjectURL(removed.preview);
            return prev.filter((f) => f.id !== pendingId);
        });
    };

    const handleScreenshot = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: { mediaSource: "screen" } });
            const track = stream.getVideoTracks()[0];
            const imageCapture = new ImageCapture(track);
            const bitmap = await imageCapture.grabFrame();
            track.stop();
            const canvas = document.createElement("canvas");
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            canvas.getContext("2d").drawImage(bitmap, 0, 0);
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
            const file = new File([blob], `capture-${new Date().toISOString().replace(/[:.]/g, "-")}.png`, { type: "image/png" });
            setPendingFiles((prev) => [...prev, { id: `pending-${Date.now()}`, file, preview: URL.createObjectURL(blob), isVideo: false }]);
            toast.success("Capture d'écran ajoutée");
        } catch (err) { if (err.name !== "AbortError") toast.error("Impossible de capturer l'écran"); }
    };

    const handleStartRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: { mediaSource: "screen" }, audio: false });
            recordedChunksRef.current = [];
            const recorder = new MediaRecorder(stream, { mimeType: "video/webm" });
            mediaRecorderRef.current = recorder;
            recorder.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
            recorder.onstop = () => {
                const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
                const file = new File([blob], `enregistrement-${new Date().toISOString().replace(/[:.]/g, "-")}.webm`, { type: "video/webm" });
                setPendingFiles((prev) => [...prev, { id: `pending-${Date.now()}`, file, preview: null, isVideo: true }]);
                toast.success("Enregistrement terminé et ajouté");
                setIsRecording(false);
                stream.getTracks().forEach((t) => t.stop());
            };
            stream.getVideoTracks()[0].onended = () => { if (recorder.state === "recording") recorder.stop(); };
            recorder.start();
            setIsRecording(true);
        } catch (err) { if (err.name !== "AbortError") toast.error("Impossible de démarrer l'enregistrement"); }
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
        }
    };

    const handleReformulateComment = async () => {
        if (!newComment.trim()) return;
        try {
            setReformulatingComment(true);
            const result = await api.reformulateComment({
                comment: newComment,
                context: "comment",
                ticket_number: ticket?.ticket_number || "",
                ticket_title: ticket?.title || "",
            });
            setNewComment(result.reformulated);
            toast.success("Commentaire reformule par l'IA");
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur IA"); }
        finally { setReformulatingComment(false); }
    };

    const handleReformulateContact = async () => {
        if (!contactSummary.trim()) return;
        try {
            setReformulatingContact(true);
            const result = await api.reformulateComment({
                comment: contactSummary,
                context: "contact_summary",
                ticket_number: ticket?.ticket_number || "",
                ticket_title: ticket?.title || "",
            });
            setContactSummary(result.reformulated);
            toast.success("Resume reformule par l'IA");
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur IA"); }
        finally { setReformulatingContact(false); }
    };

    const handleContactSubmit = async () => {
        if (!contactSummary.trim() || !contactMode) return;
        try {
            setSubmittingContact(true);
            const ct = CONTACT_TYPES[contactMode];
            const contactAtValue = contactAt || new Date().toISOString();
            await api.createComment(ticketId, { content: `${ct.label} : ${contactSummary}`, is_internal: false, contact_type: contactMode, contact_summary: contactSummary, contact_at: contactAtValue }, currentUser.id);
            setContactSummary("");
            setContactAt("");
            setContactMode(null);
            toast.success("Prise de contact enregistree");
            onRefresh();
        } catch { toast.error("Erreur lors de l'enregistrement"); }
        finally { setSubmittingContact(false); }
    };

    return (
        <div>
            {/* Comment List */}
            <div className="space-y-4 mb-6">
                {comments.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">Aucun commentaire</p>
                ) : (
                    comments.map((comment) => {
                        const author = getUserById(comment.author_id);
                        if (comment.is_system) {
                            return (
                                <div key={comment.id} className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-md border border-gray-100" data-testid={`comment-${comment.id}`}>
                                    <Info className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    <p className="text-sm text-gray-600">{comment.content}</p>
                                    <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{formatRelativeTime(comment.created_at)}</span>
                                </div>
                            );
                        }
                        if (comment.contact_type) {
                            const ct = {
                                email: { icon: Mail, label: "E-mail envoyé", bg: "bg-blue-50 border-blue-200", iconColor: "text-blue-500" },
                                phone: { icon: Phone, label: "Appel téléphonique", bg: "bg-green-50 border-green-200", iconColor: "text-green-500" },
                                visit: { icon: UserCheck, label: "Visite en personne", bg: "bg-purple-50 border-purple-200", iconColor: "text-purple-500" },
                            }[comment.contact_type] || { icon: MessageSquare, label: "Contact", bg: "bg-gray-50 border-gray-200", iconColor: "text-gray-500" };
                            const ContactIcon = ct.icon;
                            return (
                                <div key={comment.id} className={`flex gap-3 p-3 rounded-lg border ${ct.bg}`} data-testid={`contact-${comment.id}`}>
                                    <div className={`w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 border ${ct.iconColor}`}>
                                        <ContactIcon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{ct.label}</span>
                                            {comment.contact_at && (
                                                <span className="text-xs font-medium opacity-60">le {new Date(comment.contact_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })} à {new Date(comment.contact_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                                            )}
                                            <span className="text-xs opacity-50">par {author?.first_name} {author?.last_name}</span>
                                            <span className="text-xs opacity-40 ml-auto">{formatRelativeTime(comment.created_at)}</span>
                                        </div>
                                        <p className="text-sm whitespace-pre-wrap">{comment.contact_summary || comment.content}</p>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div key={comment.id} className={`flex gap-3 ${comment.is_internal ? "bg-yellow-50 p-3 rounded-lg" : ""}`} data-testid={`comment-${comment.id}`}>
                                <UserAvatar user={author} size="md" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-medium">{author?.first_name} {author?.last_name}</span>
                                        <span className="text-xs text-gray-400">{formatRelativeTime(comment.created_at)}</span>
                                        {comment.is_internal && <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded">Interne</span>}
                                    </div>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Contact tracking buttons */}
            <div className="print:hidden">
                {isAgentOrAdmin && !contactMode && (
                    <div className="flex items-center gap-2 mb-3" data-testid="contact-buttons">
                        <span className="text-xs font-medium text-gray-500 mr-1">Prise de contact :</span>
                        <button type="button" onClick={() => { setContactMode("email"); setContactAt(localNow()); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" data-testid="contact-email-btn"><Mail className="w-3.5 h-3.5" />E-mail</button>
                        <button type="button" onClick={() => { setContactMode("phone"); setContactAt(localNow()); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors" data-testid="contact-phone-btn"><Phone className="w-3.5 h-3.5" />Appel</button>
                        <button type="button" onClick={() => { setContactMode("visit"); setContactAt(localNow()); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors" data-testid="contact-visit-btn"><UserCheck className="w-3.5 h-3.5" />Visite</button>
                    </div>
                )}

                {contactMode && (
                    <div className={`p-3 rounded-lg border mb-3 ${contactMode === "email" ? "bg-blue-50 border-blue-200" : contactMode === "phone" ? "bg-green-50 border-green-200" : "bg-purple-50 border-purple-200"}`} data-testid="contact-form">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold uppercase tracking-wide">{CONTACT_TYPES[contactMode]?.label}</span>
                            <button type="button" onClick={() => { setContactMode(null); setContactSummary(""); setContactAt(""); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="mb-2">
                            <label className="text-xs font-medium text-gray-600 mb-1 block">Date et heure du contact</label>
                            <input type="datetime-local" value={contactAt} onChange={(e) => setContactAt(e.target.value)} className="w-full sm:w-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid="contact-datetime-input" />
                        </div>
                        <Textarea placeholder={contactMode === "email" ? "Contenu / objet du mail envoyé..." : contactMode === "phone" ? "Ce qui a été dit / convenu au téléphone..." : "Ce qui a été conclu lors de la visite..."} value={contactSummary} onChange={(e) => setContactSummary(e.target.value)} className="min-h-[80px] bg-white mb-2" data-testid="contact-summary-input" autoFocus />
                        <div className="flex items-center justify-end gap-2">
                            {aiEnabled && (
                                <Button type="button" variant="outline" size="sm"
                                    onClick={handleReformulateContact}
                                    disabled={!contactSummary.trim() || reformulatingContact}
                                    className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50"
                                    data-testid="ai-reformulate-contact-btn">
                                    {reformulatingContact ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                    Reformuler
                                </Button>
                            )}
                            <Button size="sm" onClick={handleContactSubmit} disabled={!contactSummary.trim() || submittingContact} data-testid="submit-contact-btn"><Send className="w-3.5 h-3.5 mr-1.5" />Enregistrer</Button>
                        </div>
                    </div>
                )}

                {/* Regular comment form with attachments */}
                <form onSubmit={handleAddComment} className="space-y-3">
                    {/* 7C — Macro quick reply button */}
                    {isAgentOrAdmin && macros.length > 0 && (
                        <div className="relative" ref={macroMenuRef}>
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowMacroMenu(!showMacroMenu)} disabled={applyingMacro}
                                className="gap-2 text-xs" data-testid="macro-menu-btn">
                                {applyingMacro ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                                Réponses rapides
                                <ChevronDown className="w-3 h-3" />
                            </Button>
                            {showMacroMenu && (
                                <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl min-w-[280px] max-h-[320px] overflow-y-auto" data-testid="macro-dropdown">
                                    {(() => {
                                        const grouped = {};
                                        macros.forEach((m) => {
                                            const cat = m.category || "general";
                                            if (!grouped[cat]) grouped[cat] = [];
                                            grouped[cat].push(m);
                                        });
                                        return Object.entries(grouped).map(([cat, items]) => (
                                            <div key={cat}>
                                                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase text-gray-400 tracking-wide bg-gray-50 border-b">{cat}</div>
                                                {items.map((macro) => (
                                                    <button key={macro.id} type="button" onClick={() => handleApplyMacro(macro)}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between gap-2 border-b border-gray-50 last:border-b-0"
                                                        data-testid={`apply-macro-${macro.id}`}>
                                                        <span className="font-medium text-gray-900">{macro.name}</span>
                                                        {macro.status_change && <StatusBadge status={macro.status_change} />}
                                                    </button>
                                                ))}
                                            </div>
                                        ));
                                    })()}
                                </div>
                            )}
                        </div>
                    )}

                    <Textarea placeholder="Ajouter un commentaire..." value={newComment} onChange={(e) => setNewComment(e.target.value)} className="min-h-[100px]" data-testid="comment-input" />

                    {pendingFiles.length > 0 && (
                        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border" data-testid="pending-files">
                            {pendingFiles.map((pf) => (
                                <div key={pf.id} className="relative group">
                                    {pf.preview ? (
                                        <img src={pf.preview} alt={pf.file.name} className="w-20 h-20 object-cover rounded-md border" />
                                    ) : (
                                        <div className="w-20 h-20 rounded-md border bg-white flex flex-col items-center justify-center gap-1">
                                            {pf.isVideo ? <Film className="w-6 h-6 text-purple-500" /> : <FileIcon className="w-6 h-6 text-gray-400" />}
                                            <span className="text-[9px] text-gray-500 text-center px-1 truncate max-w-full">{pf.file.name.length > 12 ? pf.file.name.slice(0, 10) + "..." : pf.file.name}</span>
                                        </div>
                                    )}
                                    <button type="button" onClick={() => removePendingFile(pf.id)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-pending-${pf.id}`}><X className="w-3 h-3" /></button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5">
                            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.log" data-testid="file-input" />
                            <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors" title="Joindre un fichier" data-testid="attach-file-btn"><Paperclip className="w-4 h-4" /><span className="hidden sm:inline">Fichier</span></button>
                            <button type="button" onClick={handleScreenshot} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Capturer l'écran" data-testid="screenshot-btn"><Camera className="w-4 h-4" /><span className="hidden sm:inline">Capture</span></button>
                            {!isRecording ? (
                                <button type="button" onClick={handleStartRecording} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors" title="Enregistrer l'écran" data-testid="record-btn"><Video className="w-4 h-4" /><span className="hidden sm:inline">Enregistrer</span></button>
                            ) : (
                                <button type="button" onClick={handleStopRecording} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors animate-pulse" data-testid="stop-record-btn"><Square className="w-4 h-4 fill-red-600" /><span>Arrêter</span></button>
                            )}
                            {isAgentOrAdmin && (
                                <label className="flex items-center gap-1.5 text-xs text-gray-500 ml-2">
                                    <Checkbox checked={isInternalComment} onCheckedChange={setIsInternalComment} data-testid="internal-comment-checkbox" />
                                    Note interne
                                </label>
                            )}
                        </div>
                        <Button type="submit" disabled={(!newComment.trim() && pendingFiles.length === 0) || submitting || uploading} data-testid="submit-comment-btn">
                            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi...</> : <><Send className="w-4 h-4 mr-2" />Envoyer</>}
                        </Button>
                        {isAgentOrAdmin && aiEnabled && (
                            <Button type="button" variant="outline" size="sm"
                                onClick={handleReformulateComment}
                                disabled={!newComment.trim() || reformulatingComment}
                                className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50"
                                data-testid="ai-reformulate-comment-btn">
                                {reformulatingComment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                Reformuler
                            </Button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
